import { Item } from '@react-stately/collections'
import { object, string } from 'yup'
import { Formik, useFormikContext } from 'formik'
import { Button } from 'blaze-ui/Button'
import { Field } from './Field'
import { ChipSelect } from 'components/ChipSelect/ChipSelect'
import { FormProps } from './Types'
import Styles from './Form.module.scss'
import { ImagePrompt } from 'types'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useState } from 'react'
import { ArrowRotateLeft2 } from 'blaze-ui/icons'
import { SnippetsInput } from 'components/Wizards/Snippets/SnippetsInput/SnippetsInput'

const schema = object({
  topic: string().required(),
  image_type: string().required().oneOf(['illustration', 'photo']).default('illustration'),
  image_aspect_ratio: string().required().oneOf(['square', 'landscape', 'portrait']).default('square'),
  model: string().required().oneOf(['mystic', 'openai']).default('mystic'),
  priority: string().required().oneOf(['speed', 'quality']).default('speed'),
})

const FormButton = ({
  isDisabled = false,
  hasGenerated,
  setHasGenerated,
}: {
  isDisabled?: boolean
  hasGenerated: boolean
  setHasGenerated: (value: boolean) => void
}) => {
  const { isMobile } = useResponsiveDesign()
  const { dirty, submitForm, resetForm, values } = useFormikContext()

  return (
    <ConditionalWrapper condition={!isMobile} wrapper={(children) => <div>{children}</div>}>
      {(!hasGenerated || (hasGenerated && dirty)) && (
        <Button
          variant={hasGenerated ? 'tertiary' : 'primary'}
          size="lg"
          fullWidth
          onPress={() => {
            submitForm()
            setHasGenerated(true)
            resetForm({ values }) // Allows dirty state to be reset
          }}
          isDisabled={isDisabled}
          data-testid="generate-image-button"
        >
          Generate Image
        </Button>
      )}
      {hasGenerated && !dirty && (
        <Button
          frontIcon={ArrowRotateLeft2}
          variant="tertiary"
          size="lg"
          fullWidth
          onPress={() => {
            submitForm()
            resetForm({ values }) // Allows dirty state to be reset
          }}
          isDisabled={isDisabled}
        >
          Try Again
        </Button>
      )}
    </ConditionalWrapper>
  )
}

export const Form = ({ isDisabled, onSubmit }: FormProps) => {
  const [hasGenerated, setHasGenerated] = useState(false)
  const { isMobile } = useResponsiveDesign()

  return (
    <Formik
      initialValues={schema.cast(schema.getDefault(), { assert: false })}
      validationSchema={schema}
      onSubmit={onSubmit}
    >
      {({ values: { topic, image_type, image_aspect_ratio, model }, setFieldValue }) => (
        <div className={Styles.form}>
          <div className={Styles.fields}>
            <Field label="Describe the scene for your image" isRequired>
              <SnippetsInput
                value={topic ?? ''}
                onChange={(e) => setFieldValue('topic', e)}
                size="xl"
                name="inputText"
                isTextarea
                className={Styles.input}
                autoFocus={!isMobile}
                placeholder="Try to be as detailed as possible..."
              />
            </Field>

            <Field label="Style" isRequired>
              <ChipSelect
                selectionMode="single"
                selectedKeys={[image_type]}
                onSelectionChange={(keys) =>
                  setFieldValue('image_type', String([...keys][0]) as ImagePrompt['image_type'])
                }
                deletable={false}
              >
                <Item key="illustration" textValue="illustration">
                  Illustration
                </Item>
                <Item key="photo" textValue="photo">
                  Photo
                </Item>
              </ChipSelect>
            </Field>

            <Field label="Aspect Ratio" isRequired>
              <ChipSelect
                selectionMode="single"
                selectedKeys={[image_aspect_ratio]}
                onSelectionChange={(keys) =>
                  setFieldValue('image_aspect_ratio', String([...keys][0]) as ImagePrompt['image_aspect_ratio'])
                }
                deletable={false}
              >
                <Item key="square" textValue="square">
                  Square
                </Item>
                <Item key="landscape" textValue="landscape">
                  Landscape
                </Item>
                <Item key="portrait" textValue="portrait">
                  Portrait
                </Item>
              </ChipSelect>
            </Field>
          </div>

          <FormButton isDisabled={isDisabled} hasGenerated={hasGenerated} setHasGenerated={setHasGenerated} />
        </div>
      )}
    </Formik>
  )
}
